<template>
    <div style="position: fixed; z-index: 1200; bottom: 0;">
        <JackpotWidget v-if="isShowJackpotWidget" v-bind:showCloseButton="false" />
        <div
            class="miniMpTicket notranslate miniParlayShow safeAreaBottom"
        >
            <div
                class="sm_titleBox"
            >
                <div class="sm_comboInfo">
                    <p class="sm_count">
                        {{ ticketCount }}
                    </p>
                    <span class="sm_comboTitle">
                        {{ comboParlaySelection }}
                    </span>
                    <span v-if="totalOdds > 0" class="sm_combo-odds">@{{ totalOddsDisplay }}</span>
                </div>
                <div class="feature-btn-container">
                    <button
                        v-if="isRemoveAllOnMiniParlayEnabled"
                        type="button"
                        v-bind:disabled="ticketCount === 0"
                        class="sm_removeall MiniParlaySlip_Btn_RemoveAll"
                        v-on:click.stop="removeBetSlip"
                    >
                        <SboIcon
                            name="system:trashOutline"
                            size="20"
                        />
                    </button>
                    <button
                        type="button"
                        class="sm_viewall MiniParlaySlip_Btn_ViewAll"
                        v-bind:disabled="!isValidSubBetCount"
                        v-on:click.stop="showBigBetSlip"
                    >
                        <SboIcon
                            name="system:expand"
                            size="20"
                        />
                    </button>
                </div>
                <button
                    type="button"
                    class="btn_close MiniParlaySlip_Btn_Collapse"
                    v-on:click.stop="collapseBetSlip"
                >
                    <SboIcon
                        name="system:arrowDown"
                        size="20"
                    />
                </button>
            </div>
            <MediumParlayBetSlipContent />
            <div class="miniMpContent MiniParlaySlip_view">
                <Message
                    v-if="message.key"
                    status="error"
                    v-bind:messageKey="message.key"
                    v-bind:messageData="message.data"
                    class="miniMpContent-message"
                />
                <div class="ticket-maxPayout">
                    <span>{{ getMaxPayoutText }} :</span>
                    <span class="ticket-amount">
                        {{ totalPayoutDisplay }}
                    </span>
                </div>
                <div class="ticket-inputArea">
                    <PlaceBet
                        v-bind:stake="stake"
                        v-bind:isDisableBtn="isDisablePlaceBet"
                        v-bind:balance="balance"
                        v-bind:minBet="betSlip.min"
                        v-bind:price="price"
                        v-on:placeBet="placeBet"
                        v-on:changeStake="setStake"
                    />
                    <StakePad
                        v-bind:stake="stake"
                        v-bind:balance="balance"
                        v-bind:minBet="betSlip.min"
                        v-bind:maxBet="betSlip.max"
                        v-bind:currency="customerInfo.currency"
                        v-on:changeStake="setStake"
                    />
                </div>
                <SwitchAcceptAnyOddsValue
                    v-if="!customerInfo.isAcceptAnyOdds"
                />
            </div>
        </div>
    </div>
</template>

<script lang="ts">
    import { storeToRefs } from 'pinia';
    import { computed, defineComponent, onMounted } from 'vue';
    import JackpotWidget from '../jackpot/JackpotWidget.vue';
    import SwitchAcceptAnyOddsValue from '@/components/SwitchAcceptAnyOddsValue.vue';
    import MediumParlayBetSlipContent from '@/components/betSlip/MediumParlayBetSlipContent.vue';
    import PlaceBet from '@/components/betSlip/PlaceBet.vue';
    import StakePad from '@/components/betSlip/StakePad.vue';
    import Message from '@/components/common/Message.vue';
    import SboIcon from '@/components/common/SboIcon.vue';
    import { useJackpotDisplay } from '@/libraries/composables/useJackpotDisplay';
    import type { IEuroTicket } from '@/libraries/euroTicket';
    import { isValidComboBet } from '@/libraries/helpers/comboParlayHelper';
    import { getMaxPayoutText } from '@/libraries/helpers/i18nHelper';
    import { useAccountStore } from '@/stores/accountStore';
    import { useBetSlipStore } from '@/stores/betSlip/betSlipStore';
    import { useParlayBetSlipStore } from '@/stores/betSlip/parlayBetSlipStore';
    import { useFeatureToggleStore } from '@/stores/featureToggleStore';

    export default defineComponent({
        name: 'MediumParlayBetSlip',
        components: {
            JackpotWidget,
            MediumParlayBetSlipContent,
            SwitchAcceptAnyOddsValue,
            PlaceBet,
            StakePad,
            Message,
            SboIcon,
        },
        setup() {
            const { isShowJackpotWidget } = useJackpotDisplay();
            const { customerInfo, balance } = storeToRefs(useAccountStore());

            const {
                setIsShowStakePad,
                showBigBetSlip,
                showMiniBetSlip,
                closeBetSlip,
            } = useBetSlipStore();

            const {
                removeBetSlip,
                placeBet,
                setStake,
                openTicket,
            } = useParlayBetSlipStore();

            const {
                betSlip,
                ticketCount,
                isValidSubBetCount,
                totalOdds,
                totalOddsDisplay,
                stake,
                isPlacingBet,
                totalPayoutDisplay,
                price,
                comboParlaySelection,
                subBets,
            } = storeToRefs(useParlayBetSlipStore());

            const { isRemoveAllOnMiniParlayEnabled } = storeToRefs(useFeatureToggleStore());

            const isDisablePlaceBet = computed(() => {
                if (isPlacingBet.value) return true;
                if (!isValidComboBet(betSlip.value, isValidSubBetCount.value)) return true;

                if (balance.value === null) return false;
                if (balance.value < betSlip.value.min) return true;

                return Math.floor(balance.value) <= 0;
            });

            const message = computed(() => {
                const messages = [
                    'betSlip_errorMessage_eventClosed',
                    'betSlip_errorMessage_oddsClosed',
                    'betSlip_errorMessage_maxOddsExceed',
                    'betSlip_errorMessage_oddsChanged',
                    'betSlip_errorMessage_pointChanged',
                    'betSlip_errorMessage_scoreChanged',
                ];
                let subBet: IEuroTicket | undefined;
                messages.forEach((key) => {
                    if (!subBet) {
                        subBet = subBets.value.find(item => item.message.key === key);
                    }
                });

                if (!subBet) {
                    subBet = subBets.value.find(item => item.message);
                }

                return subBet?.message ?? { key: null, data: null };
            });

            setIsShowStakePad(true);

            function collapseBetSlip() {
                if (ticketCount.value === 0) {
                    closeBetSlip();
                } else {
                    showMiniBetSlip();
                }
            }

            onMounted(async () => {
                if (isValidSubBetCount.value) {
                    await openTicket(true);
                }
            });

            return {
                isShowJackpotWidget,
                isRemoveAllOnMiniParlayEnabled,
                ticketCount,
                isValidSubBetCount,
                comboParlaySelection,
                totalOdds,
                totalOddsDisplay,
                showBigBetSlip,
                collapseBetSlip,
                removeBetSlip,
                customerInfo,
                message,
                placeBet,
                setStake,
                stake,
                isDisablePlaceBet,
                balance,
                betSlip,
                totalPayoutDisplay,
                getMaxPayoutText,
                price,
            };
        },
    });
</script>

<style lang="scss" scoped>
    @import "@/scss/components/miniMpTicket";
</style>

<style lang="scss">
    @import "@/scss/components/miniMpTicketContent";
</style>
